// src/services/commons.service.ts

import { useCtxStore } from '@/stores/ctx.store'
import { createBrowserInspector } from '@statelyai/inspect'
import type { GetContextMenuItemsParams, GetMainMenuItemsParams, MenuItemDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { IRowNode } from 'ag-grid-enterprise'
import consola from 'consola'
import moment from 'moment'
import { defineStore } from 'pinia'
import { type FinancialsGridColData, FpnaDataType } from '~/composables/financials-grid-commons'
import { useAppMachine } from '~/composables/useAppMachine'
import { useNumberFormat } from '~/composables/useNumberFormat'
import type { StcsCompData } from '~/protos/gen/stcs_comp.entity'
import type { StcsCompActualsResValuesItem } from '~/protos/gen/stcs_comp_actuals_res_values.entity'
import { MethodologyEnum } from '~/protos/gen/stcs_comp_plan_cfg.entity.ts'
import type { StcsCompPlanResValuesItem } from '~/protos/gen/stcs_comp_plan_res_values.entity'
import { StcsFpnaTypeEnum, StcsFstmtEnum } from '~/protos/gen/stcs_enums.entity'
import type { StcsOrgRoleEnum } from '~/protos/gen/stcs_enums.entity'
import type { StcsOrgOnboarding } from '~/protos/gen/stcs_org.entity'
import type { StcsOrgFsdFlatItem } from '~/protos/gen/stcs_org_fsd_flat.entity'
import { DecimalSeparatorEnum, NegativeNumberFormatEnum, NumberFormatEnum, type NumberFormatOptions } from '~/protos/gen/stcs_user.entity'
import { type CustomTreeNode, useAcmStore } from '~/stores/acm.store.ts'
import { useFsdStore } from '~/stores/fsd.store'
import { createPlanningViewStore } from '~/stores/planningview.store.ts'
import { urlCtxPartObj } from '~/types/route.entity'
import type { FinancialsGridRowData } from '~/types/stcs_fpna_res_values.entity'
import { type PlanningRowData, computed, useNuxtApp } from '#imports'
import delang from '../../public/i18n/de.json'
import enlang from '../../public/i18n/en.json'

export function isObjectEmpty(obj: object) {
  return Object.keys(obj).length === 0
}

export function formatCurrencyValue(value: number, format?: NumberFormatOptions): string {
  // If format is explicitly provided, use it (for backward compatibility)
  // Otherwise, use the org-specific format from the composable
  const numberFormat = format || useNumberFormat().value
  return createFormatter({})(value, numberFormat)
}

export function formatCurrencyCtxValue(value: number, format?: NumberFormatOptions): string {
  // If format is explicitly provided, use it (for backward compatibility)
  // Otherwise, use the org-specific format from the composable
  const numberFormat = format || useNumberFormat().value
  return createFormatter({ style: 'percent' })(value, numberFormat)
}

export function getDisplayValueFromActuals(colDataObj: FinancialsGridColData, rowClass: string, values_map: { [key: string]: number }, format: NumberFormatOptions | undefined, row: PlanningRowData): string {
  const rawVal = values_map[colDataObj.dateStr]

  if (rawVal == null || Number.isNaN(rawVal)) {
    if (isValidPlanningCustomRow(row) || [StcsFpnaTypeEnum.SFT_CTX_LINE_IPO, StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH, StcsFpnaTypeEnum.SFT_LEVEL_2].includes(row.fpna_type as StcsFpnaTypeEnum)) {
      return ''
    }

    return formatCurrencyValue(0, format)
  }

  if (rowClass.includes('ctx-line-percent-child')) {
    return formatCurrencyCtxValue(rawVal, format)
  }

  return formatCurrencyValue(rawVal, format)
}

function getDisplayValueFromComparisonActuals(colDataObj: FinancialsGridColData, values_map: { [key: string]: number }, format: NumberFormatOptions | undefined, withOutFormat = false): string {
  const value: number = values_map[colDataObj.colId] ?? 0
  return withOutFormat ? value.toString() : formatCurrencyValue(value, format)
}

function getDisplayValueFromAipo(colDataObj: FinancialsGridColData, values_map: { [key: string]: number }, format: NumberFormatOptions | undefined, withOutFormat = false): string {
  let value = values_map[colDataObj.colId]
  // const i18_not_meaningful_number = $tolgee.t('not_meaningful_number')
  if (value === undefined) {
    value = 0
  }
  return withOutFormat ? value.toString() : formatCurrencyCtxValue(value, format)
}

export function returnInvertedValues(values_map: { [key: string]: number }, invertFactor: number): { [key: string]: number } {
  return Object.fromEntries(Object.entries(values_map).map(([key, value]) => [key, value != null && !Number.isNaN(value) ? value * invertFactor : value]))
}

export function valueGetter(params: ValueGetterParams<FinancialsGridRowData> | ValueGetterParams<PlanningRowData>, data_map: { [key: string]: StcsCompActualsResValuesItem } | undefined, withOutFormat = false): string {
  if (!(params.data && data_map) || params.column.getColId() === 'ag-Grid-AutoColumn') {
    return ''
  }

  const colDataObj: FinancialsGridColData = params.column.getUserProvidedColDef() as FinancialsGridColData
  if (!colDataObj) {
    consola.error('colDataObj is undefined, exposing redundant aggrid call')
    return ''
  }

  const row_data = data_map[params.data.row_id]
  if (!row_data?.values_map) {
    return ''
  }
  const rowClass = params.data.rowClass

  const values_map = row_data.values_map
  const invertFactor = params.data?.options?.invert_sum_value ?? 1
  const invertedValuesMap = returnInvertedValues(values_map, invertFactor)

  const parentNode = params?.node?.parent as IRowNode<FinancialsGridRowData & { selectedMethod?: number }>
  const parentSelectedMethod = parentNode?.data?.selectedMethod
  if (params.data?.rowClass === 'selected-method-row' && parentSelectedMethod === MethodologyEnum.MONTHLY_GROWTH_RATE && !(params.colDef.headerClass as string)?.includes('header-actuals')) {
    const raw = values_map[colDataObj.dateStr]
    return raw != null ? raw.toString() : ''
  }

  const { $tolgee } = useNuxtApp()
  const not_available = $tolgee.t('not_available')

  // Determine if this is a CTX row (percentage values should not be inverted)
  const isCtxRow = rowClass.includes('ctx-line-percent-child') || rowClass.includes('total-cml-ctx-line-percent-child')

  // Use appropriate values map based on data type and row type
  const valuesMapForActuals = isCtxRow ? values_map : invertedValuesMap

  // Get org-specific number format settings
  const numberFormatOptions = useNumberFormat().value

  const fstmtDataTypeActions: Record<FpnaDataType, () => string> = {
    [FpnaDataType.ACTUALS]: () => getDisplayValueFromActuals(colDataObj, rowClass, valuesMapForActuals, numberFormatOptions, params.data as PlanningRowData),
    [FpnaDataType.PLAN]: () => getDisplayValueFromActuals(colDataObj, rowClass, valuesMapForActuals, numberFormatOptions, params.data as PlanningRowData),
    [FpnaDataType.COMPARISON_ACTUALS]: () => getDisplayValueFromComparisonActuals(colDataObj, invertedValuesMap, numberFormatOptions),
    [FpnaDataType.COMPARISON_PLAN]: () => getDisplayValueFromComparisonActuals(colDataObj, invertedValuesMap, numberFormatOptions),
    [FpnaDataType.COMPARISON_DIFF]: () => getDisplayValueFromComparisonActuals(colDataObj, invertedValuesMap, numberFormatOptions, withOutFormat),
    [FpnaDataType.COMPARISON_AIPO]: () => getDisplayValueFromAipo(colDataObj, values_map, numberFormatOptions),
    [FpnaDataType.COMPARISON_PIPO]: () => getDisplayValueFromAipo(colDataObj, values_map, numberFormatOptions),
    [FpnaDataType.COMPARISON_DIPO]: () => getDisplayValueFromAipo(colDataObj, values_map, numberFormatOptions, withOutFormat),
    [FpnaDataType.COMPARISON_FORECAST]: () => not_available,
  }

  return fstmtDataTypeActions[colDataObj?.fstmtDataType]?.() ?? not_available
}

function extractValidNumber(row: { [key: string]: number }, key: string): number | null {
  const v = row[key]
  return v == null || Number.isNaN(v) ? null : v
}

export function valueGetterNumber(params: ValueGetterParams<PlanningRowData>, data_map?: Record<string, StcsCompActualsResValuesItem>): number | null {
  if (!(params.data && data_map) || params.column.getColId() === 'ag-Grid-AutoColumn') {
    return null
  }

  const colDef = params.column.getUserProvidedColDef() as FinancialsGridColData
  const row = data_map[params.data.row_id]?.values_map
  if (!row) {
    return null
  }
  const invertFactor = params.data?.options?.invert_sum_value ?? 1
  const invertedValuesMap = returnInvertedValues(row, invertFactor)

  const dateKey = colDef.dateStr
  const parent = params.node ? params.node.parent : undefined

  if (params.data.rowClass === 'selected-method-row' && parent?.data?.selectedMethod === MethodologyEnum.MONTHLY_GROWTH_RATE && !(colDef.headerClass as string)?.includes('header-actuals')) {
    return extractValidNumber(invertedValuesMap, dateKey)
  }

  // Determine if this is a CTX row (percentage values should not be inverted)
  const isCtxRow = params.data.rowClass.includes('ctx-line-percent-child') || params.data.rowClass.includes('total-cml-ctx-line-percent-child')

  switch (colDef.fstmtDataType) {
    case FpnaDataType.ACTUALS:
      // For actuals, use original values for CTX rows, inverted for others
      return extractValidNumber(isCtxRow ? row : invertedValuesMap, dateKey)
    case FpnaDataType.COMPARISON_ACTUALS:
      // Comparison actuals are always inverted (non-percentage data)
      return extractValidNumber(invertedValuesMap, dateKey)
    case FpnaDataType.COMPARISON_AIPO:
    case FpnaDataType.COMPARISON_PIPO:
    case FpnaDataType.COMPARISON_DIPO:
      // All percentage comparison data types are never inverted
      return extractValidNumber(row, dateKey)
    default:
      return null
  }
}

export function valueFormatterString(params: ValueFormatterParams<PlanningRowData>, data_map?: Record<string, StcsCompActualsResValuesItem>): string {
  if (!(params.data && data_map) || params.column.getColId() === 'ag-Grid-AutoColumn') {
    return ''
  }

  const colDef = params.column.getUserProvidedColDef() as FinancialsGridColData
  const row = data_map[params.data.row_id]?.values_map
  if (!row) {
    return ''
  }

  const { $tolgee } = useNuxtApp()
  const not_available = $tolgee.t('not_available')
  const fmtOpts = useNumberFormat().value
  const rowClass = params.data.rowClass
  const dateKey = colDef.dateStr
  const invertFactor = params.data?.options?.invert_sum_value ?? 1
  const invertedValuesMap = returnInvertedValues(row, invertFactor)

  // monthly-growth-special
  const parent = params.node ? params.node.parent : undefined
  if (rowClass === 'selected-method-row' && parent?.data?.selectedMethod === MethodologyEnum.MONTHLY_GROWTH_RATE && !(colDef.headerClass as string)?.includes('header-actuals')) {
    const v = row[dateKey]
    return v != null ? v.toString() : ''
  }

  // Determine if this is a CTX row (percentage values should not be inverted)
  const isCtxRow = rowClass.includes('ctx-line-percent-child') || rowClass.includes('total-cml-ctx-line-percent-child')

  // Use appropriate values map based on data type and row type
  const valuesMapForActuals = isCtxRow ? row : invertedValuesMap

  switch (colDef.fstmtDataType) {
    case FpnaDataType.ACTUALS:
      return getDisplayValueFromActuals(colDef, rowClass, valuesMapForActuals, fmtOpts, params.data)
    case FpnaDataType.COMPARISON_ACTUALS:
      return getDisplayValueFromComparisonActuals(colDef, invertedValuesMap, fmtOpts)
    case FpnaDataType.COMPARISON_AIPO:
    case FpnaDataType.COMPARISON_PIPO:
    case FpnaDataType.COMPARISON_DIPO:
      return getDisplayValueFromAipo(colDef, row, fmtOpts)
    default:
      return not_available
  }
}

export const useCmns = defineStore('cmns', {
  state: () => ({}),
  actions: {
    handleDisconnect() {
      const { $clerk, $posthog } = useNuxtApp()
      const $config = useRuntimeConfig()
      this.clearStorage()
      if (!$clerk) {
        consola.error('🔴 $clerk is undefined')
        $posthog().captureException(`🔴 WAPP v${$config.public.appVersion} | commons | handleDisconnect | $clerk undefined`)
        window.location.href = urlCtxPartObj.ROOT.path
        return
      }
      $clerk.client
        ?.removeSessions()
        .then(() => {
          window.location.href = urlCtxPartObj.ROOT.path
        })
        .catch((err) => {
          consola.error('Error disconnecting: ', err)
          $posthog().captureException(`🔴 WAPP v${$config.public.appVersion} | commons | handleDisconnect:removeSessions | ${JSON.stringify({ error: err?.message })}`)
          window.location.href = urlCtxPartObj.ROOT.path
        })
    },
    clearStorage() {
      // Save the app-store data temporarily
      const appStoreData = sessionStorage.getItem('app-store')

      // Clear all session storage
      sessionStorage.clear()

      // Restore the app-store data if it exists
      if (appStoreData) {
        sessionStorage.setItem('app-store', appStoreData)
      }

      // Clear all local storage
      localStorage.clear()
    },
  },
})

export function getInitials(firstName: string, lastName: string): string {
  if (!(firstName && lastName)) {
    return ''
  }

  const firstInitial = firstName.trim()[0].toUpperCase()
  const lastInitial = lastName.trim()[0].toUpperCase()

  return firstInitial + lastInitial
}

export function getLanguage(lang: string): string {
  const findLang = useAppMachine().locales.value.find((val: string) => val.toLowerCase() === lang.toLowerCase())

  if (!(lang && findLang)) {
    return 'en'
  }

  return findLang
}

function getScale(style: string | undefined, numberFormat?: NumberFormatEnum): number {
  if (style === 'percent') {
    return 1
  }
  switch (numberFormat) {
    case NumberFormatEnum.NFO_TAUSEND:
      return 1_000
    case NumberFormatEnum.NFO_MILLION:
      return 1_000_000
    default:
      return 1
  }
}

export function createFormatter(options: { style?: 'decimal' | 'percent' | 'currency' }) {
  const { style = undefined } = options

  return function formatValue(value: number, format: NumberFormatOptions | undefined): string {
    const fractionDigits = style === 'percent' ? format?.percentage_decimal_places : format?.decimal_places

    const intlFormatter = new Intl.NumberFormat(format?.decimal_separator === DecimalSeparatorEnum.DS_POINT ? 'en-US' : 'de-DE', {
      style,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
      useGrouping: true,
    })

    const divisor = getScale(style, format?.number_format)
    const scaled = value / divisor

    const isNegative = scaled < 0
    const result = intlFormatter.format(Math.abs(scaled))

    if (isNegative) {
      if (format?.negative_number_format === NegativeNumberFormatEnum.NNF_BRACKETS) {
        return `(${result})`
      }
      return `-${result}`
    }
    // Append a hair-space (\u200A) only when using bracket notation for negatives,
    // so that positive numbers visually align with "(value)" in the grid column.
    // This character is not treated as whitespace by Excel and does not appear as a trailing space.
    if (format?.negative_number_format === NegativeNumberFormatEnum.NNF_BRACKETS) {
      return `${result}\u200A`
    }
    return result
  }
}

export function getYearByMonth(): { value: number; label: string }[] {
  const { $tolgee } = useNuxtApp()

  const yearMonth = $tolgee
    .t('Year-end')
    .split('\n')
    ?.map((item, i) => ({
      value: i + 1,
      label: item,
    }))

  return yearMonth
}

export const convertFileToUint8Array = (file: Blob): Promise<Uint8Array> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result))
      } else {
        reject(new Error('Failed to read file as ArrayBuffer'))
      }
    }

    reader.onerror = () => reject(reader.error)
    reader.readAsArrayBuffer(file)
  })
}

export function getCurrencyLabel(currency = '') {
  const compCurrency = currency ? currency.toUpperCase() : useCtxStore().ref_comp_map[useCtxStore().active_comp_id]?.ref_comp.data?.fpna_currency.toUpperCase()
  const numberFormat = useNumberFormat().value?.number_format
  const lang = useCtxStore().ref_user?.settings?.sel_lang.toUpperCase()

  if (numberFormat === NumberFormatEnum.NFO_TAUSEND) {
    return lang === 'EN' ? `${compCurrency}k` : `T${compCurrency}`
  }
  if (numberFormat === NumberFormatEnum.NFO_MILLION) {
    return lang === 'EN' ? `${compCurrency}m` : `M${compCurrency}`
  }

  return compCurrency
}

export function getCurrencyLabelForCompany(currency = '') {
  // Reuse logic
  return getCurrencyLabel(currency)
}

export function getIndustries(): { value: string; label: string }[] {
  const industriesObj = JSON.parse(useAppMachine().locale.value === 'en' ? enlang['onboarding-industries-obj'] : delang['onboarding-industries-obj'])
  return Object.entries(industriesObj)?.map(([key, value]) => ({ value: key.toLowerCase(), label: value as string }))
}

export function getCurrencies(): { value: string; label: string }[] {
  const { $tolgee } = useNuxtApp()

  return $tolgee
    .t('onboarding-uploaddata-currency-codes')
    .split('\n')
    .map((item) => ({
      value: item.toLowerCase(),
      label: item,
    }))
}

export function getPermission(roles: StcsOrgRoleEnum[]) {
  const roleMeta = useCtxStore()?.ref_org_map[useCtxStore()?.active_org_id]?.meta?.org_role_map[useCtxStore()?.ref_user?.id.extractId()]

  return roleMeta ? roles.includes(roleMeta) : false
}

export function getOrgIdForSandbox(orgId: string = useCtxStore()?.active_org_id): string {
  const orgMap = useCtxStore()?.ref_org_map

  return Object.entries(orgMap).find(([, org]) => org?.onboarding?.ref_id_org_sandbox === orgId)?.[0] ?? ''
}

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

export function getRandomIntInRange(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

// Total time range min 8 to max 12 seconds
export const intervals = [
  getRandomIntInRange(500, 1000), // Step 1: 0.5–1s (Quick feedback)
  getRandomIntInRange(700, 1200), // Step 2: 0.7–1.2s (Building anticipation)
  getRandomIntInRange(4000, 6000), // Step 3: 4–6s (Main processing, longest)
  getRandomIntInRange(1000, 2000), // Step 4: 1–2s (Short pause, almost done)
  getRandomIntInRange(1000, 2000), // Step 5: 1–2s (Very quick finish)
]

export function getOrgFirstComp(id: string, comp_ip: string): StcsCompData {
  const ctxStore = useCtxStore()

  const CompId = comp_ip ?? Object.keys(ctxStore.ref_org_map?.[id]?.meta?.ref_comp_map || {})?.at(0)

  if (!CompId) {
    return {} as StcsCompData
  }

  const compData = ctxStore.ref_comp_map?.[CompId]?.ref_comp?.data

  return (compData ?? {}) as StcsCompData
}

export function getNextOnboardingStepUrl(onboarding: StcsOrgOnboarding, orgId: string): string {
  // If onboarding is completed, return the complete page URL (as requested)
  if (onboarding.is_completed) {
    return urlCtxPartObj.ONBOARDING_COMPLETE.path.replace(':orgId', orgId)
  }

  // Return the first step that is NOT done.
  // 1. Welcome
  if (!onboarding.steps_map?.welcome?.done) {
    return urlCtxPartObj.WELCOME.path.replace(':orgId', orgId)
  }
  // 2. Upload
  if (!onboarding.steps_map?.upload?.done) {
    return urlCtxPartObj.ONBOARDING_UPLOAD_DATA.path.replace(':orgId', orgId)
  }
  // 3. FSD Edit
  if (!onboarding.steps_map?.fsd_edit?.done) {
    return urlCtxPartObj.ONBOARDING_FINSTMT_DESIGN.path.replace(':orgId', orgId)
  }
  // 4. FSD Confirm
  if (!onboarding.steps_map?.fsd_confirm?.done) {
    return urlCtxPartObj.ONBOARDING_FINSTMT_DESIGN_CONFIRM.path.replace(':orgId', orgId)
  }
  // 5. Mapping Edit
  if (!onboarding.steps_map?.mapping_edit?.done) {
    return urlCtxPartObj.ONBOARDING_FINSTMT_MAPPING.path.replace(':orgId', orgId)
  }
  // 6. Mapping Confirm
  if (!onboarding.steps_map?.mapping_confirm?.done) {
    return urlCtxPartObj.ONBOARDING_FINSTMT_MAPPING_CONFIRM.path.replace(':orgId', orgId)
  }
  // 7. Complete (If not done, go here)
  if (!onboarding.steps_map?.complete?.done) {
    return urlCtxPartObj.ONBOARDING_COMPLETE.path.replace(':orgId', orgId)
  }

  // Fallback: Default to Complete page if everything seems done but is_completed was false
  return urlCtxPartObj.ONBOARDING_COMPLETE.path.replace(':orgId', orgId)
}
export function buildHierarchy(items: StcsOrgFsdFlatItem[]) {
  const fsdStore = useFsdStore()

  const map: { [key: string]: CustomTreeNode } = {}

  for (const item of items) {
    map[item.row_id] = {
      label: fsdStore.getDisplayNameBylevelId(item).name,
      data: item,
      children: [],
    }
  }

  const roots: CustomTreeNode[] = []

  for (const item of items) {
    const { path_level, row_id } = item
    // If the item has a "parent" in path_level, link it
    if (path_level.length > 1) {
      const parentId = path_level[path_level.length - 2]
      // Link child to its parent (if parent is in map)
      if (map[parentId]) {
        map[parentId].children.push(map[row_id])
      } else {
        // If somehow we can't find the parent in map, treat as root
        roots.push(map[row_id])
      }
    } else {
      // If path_level is only 1 deep, there's no parent, so it's a root
      roots.push(map[row_id])
    }
  }

  return roots
}

const getRowDisplayNameForPlanning = (item: PlanningRowData): string => {
  const ctx = useCtxStore()
  const fsdStore = useFsdStore()
  const planningStore = createPlanningViewStore(ctx.active_comp_id)
  const { $tolgee } = useNuxtApp()

  const locale = useAppMachine().locale
  const activeOrg = ctx.active_org_id
  const ctxI18n = ctx.ref_fsd_map[activeOrg]?.i18n_map

  const plani18Map = planningStore.selectedPlanID ? planningStore.planConfigCache[planningStore.selectedPlanID]?.stcs_comp_plan_cfg?.i18n_map[activeOrg].label_map : undefined

  const i18nSource = ctxI18n || fsdStore.i18nMap

  const displayNameMap = computed<Record<string, string>>(() => {
    return i18nSource[locale.value]?.label_map ?? {}
  })

  const positionName = plani18Map?.[item?.row_id] || i18nSource[activeOrg]?.label_map[item?.level_id] || displayNameMap.value[item?.level_id] || $tolgee.t('not_available')

  return positionName
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
export function getRowDisplayName(item: StcsOrgFsdFlatItem | PlanningRowData, fsd = false): { name: string; isAlternate: boolean } {
  const { $tolgee } = useNuxtApp()
  // Stores
  const ctx = useCtxStore()
  const acmStore = useAcmStore()
  const fsdStore = useFsdStore()

  const locale = useAppMachine().locale
  const activeOrg = ctx.active_org_id
  const ctxI18n = ctx.ref_fsd_map[activeOrg]?.i18n_map

  const i18nSource = fsd ? fsdStore.i18nMap : ctxI18n || fsdStore.i18nMap

  const displayNameMap = computed<Record<string, string>>(() => {
    return i18nSource[locale.value]?.label_map ?? {}
  })

  const alternateDisplayNameMap = computed<Record<string, string>>(() => {
    return i18nSource[locale.value === 'en' ? 'de' : 'en']?.label_map ?? {}
  })

  // Handle Planning Rows separately
  if (isValidPlanningCustomRow(item) && !fsd) {
    const name = getRowDisplayNameForPlanning(item as PlanningRowData)
    return { name, isAlternate: false }
  }

  const displayName = { name: displayNameMap.value[item?.row_id], isAlternate: false }

  if (!displayName.name && alternateDisplayNameMap.value[item?.row_id]) {
    displayName.name = alternateDisplayNameMap.value[item?.row_id]
    displayName.isAlternate = true
  } else if (!displayName.name) {
    displayName.name = displayNameMap.value[item?.level_id] || $tolgee.t('not_available')
  }

  const nameMap = displayName.isAlternate ? alternateDisplayNameMap.value : displayNameMap.value
  const prefix = item?.ref_id_prefix ? nameMap[item?.ref_id_prefix] : undefined

  displayName.name = prefix ? `${prefix} ${displayName.name}` : displayName.name

  if (item?.ref_id_mirror) {
    displayName.name = processMirrorReference(item, nameMap, fsd) // added fsd param so that it shows in financials/cf grid
  }

  if (item?.fpna_type === StcsFpnaTypeEnum.SFT_CTX_LINE_IPO || item?.fpna_type === StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH) {
    displayName.name = item?.fpna_type === StcsFpnaTypeEnum.SFT_CTX_LINE_GROWTH ? nameMap[item?.level_id] : `${nameMap[item?.level_id]} ${nameMap[item?.sum_ref ?? '']}`
  }

  if (item?.fpna_type === StcsFpnaTypeEnum.SFT_ACC_ITEM && !fsd) {
    const levelId = item?.level_id

    const accountsDisplayNameMap = computed<Record<string, string>>(() => acmStore.i18n_map[activeOrg]?.label_map ?? {})
    const name = accountsDisplayNameMap.value[item?.level_id] ?? $tolgee.t('not_available')

    displayName.name = `${levelId} ${name}`
  }

  if (item?.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_3 && !fsd) {
    const levelId = item?.level_id

    const accountsDisplayNameMap = computed<Record<string, string>>(() => acmStore.i18n_map[locale.value]?.label_map ?? {})
    const alternateAccountsDisplayNameMap = computed<Record<string, string>>(() => acmStore.i18n_map[locale.value === 'en' ? 'de' : 'en']?.label_map ?? {})

    if (accountsDisplayNameMap.value[levelId]) {
      displayName.name = accountsDisplayNameMap.value[levelId]
    } else if (alternateAccountsDisplayNameMap.value[levelId]) {
      displayName.name = alternateAccountsDisplayNameMap.value[levelId]
      displayName.isAlternate = true
    } else {
      displayName.name = $tolgee.t('not_available')
    }
  }

  if (item?.rowClass === 'selected-method-row') {
    const localeKey = locale.value as 'en' | 'de'
    displayName.name = getLabels(item?.level_id)?.[localeKey]
  }

  return displayName
}

function processMirrorReference(item: StcsOrgFsdFlatItem, displayNameMap: Record<string, string>, fsd = false): string {
  const { $tolgee } = useNuxtApp()

  const prefix = item.ref_id_prefix ? displayNameMap[item.ref_id_prefix] : undefined

  if (!item.ref_id_mirror) {
    return $tolgee.t('not_available')
  }

  const displayName = findRootDisplayName(item?.ref_id_mirror, displayNameMap, fsd)

  return prefix ? `${prefix} ${displayName}` : displayName
}

function findRootDisplayName(ref_id: string, displayNameMap: Record<string, string>, fsd = false): string {
  const { $tolgee } = useNuxtApp()
  const ctx = useCtxStore()

  // First, check the provided displayNameMap for a direct match.
  if (ref_id in displayNameMap) {
    return displayNameMap[ref_id]
  }

  // Define all possible lists to search for the mirrored row.
  const compId = ctx.active_comp_id
  const orgId = ctx.active_org_id

  const companyFsd = compId ? ctx.ref_comp_map[compId]?.ref_comp_actuals_res_fstmt : undefined
  const orgFsd = orgId ? ctx.ref_fsd_map[orgId] : undefined

  // Create a comprehensive list of all rows to search, prioritizing company-specific data.
  const searchLists = [...(companyFsd?.pnl_list || []), ...(companyFsd?.bs_list || []), ...(companyFsd?.cf_list || []), ...(orgFsd?.pnl_list || []), ...(orgFsd?.bs_list || []), ...(orgFsd?.cf_list || [])]

  // If we are in the FSD editor, also include the FSD store's lists.
  if (fsd) {
    const fsdStore = useFsdStore()
    searchLists.push(...fsdStore.fstmtPnlList, ...fsdStore.fstmtBsList, ...fsdStore.fstmtCfList)
  }

  // Find the item with the matching row_id in the comprehensive list.
  const item = searchLists.find(({ row_id }) => row_id === ref_id)

  if (item) {
    // If the found item is itself a mirror, recurse. Otherwise, return its name.
    return item.ref_id_mirror ? processMirrorReference(item, displayNameMap, fsd) : displayNameMap[item.row_id] || displayNameMap[item.level_id] || $tolgee.t('not_available')
  }

  // If no item is found after searching all sources, return not available.
  return $tolgee.t('not_available')
}

export function updateI18nLabelMap(option: string, labels: { en: string; de: string }) {
  const ctxStore = useCtxStore()
  const orgId = ctxStore.active_org_id

  const planningStore = createPlanningViewStore(ctxStore.active_comp_id)
  const i18nMap = ctxStore.ref_fsd_map?.[orgId]?.i18n_map

  const plani18Map = planningStore.selectedPlanID ? planningStore.planConfigCache[planningStore.selectedPlanID]?.stcs_comp_plan_cfg?.i18n_map[orgId].label_map : undefined

  // Update English label map
  if (i18nMap.en?.label_map) {
    i18nMap.en.label_map[option] = labels.en
  }

  // Update German label map
  if (i18nMap.de?.label_map) {
    i18nMap.de.label_map[option] = labels.de
  }

  // Update org-specific label map (fallback to English)
  if (i18nMap[orgId]?.label_map) {
    i18nMap[orgId].label_map[option] = labels.en || labels.de
  } else {
    i18nMap[orgId] = { label_map: { [option]: labels.en || labels.de } }
  }

  // update the plans Map if the option exists there
  if (plani18Map) {
    plani18Map[option] = labels.en || labels.de
  }
}

export function cleanString(str: string) {
  return (
    str
      // biome-ignore lint/suspicious/noControlCharactersInRegex: <explanation>
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // control chars
      .replace(/[\u00A0\u1680\u2000-\u200F\u202F\u205F\u3000\u180E\uFEFF]/g, ' ') // odd spaces
      .replace(/\s+/g, ' ') // collapse runs
      .trim()
  )
}

export function getMonthPeriodsInRange(start: string, end: string): string[] {
  const [startYear, startMonth] = start.split('-').map(Number)
  const [endYear, endMonth] = end.split('-').map(Number)

  const periods: string[] = []
  let year = startYear
  let month = startMonth

  while (year < endYear || (year === endYear && month <= endMonth)) {
    periods.push(`${year}-${String(month).padStart(2, '0')}`)
    month++
    if (month > 12) {
      month = 1
      year++
    }
  }

  return periods
}

export function findRowByEndsWith(list: PlanningRowData[] | StcsOrgFsdFlatItem[], suffix: string): string {
  return list?.find((row) => row.row_id.endsWith(suffix))?.row_id ?? ''
}

export function getDynamicDefaultRefId(fsd: PlanningRowData[] | StcsOrgFsdFlatItem[], fstmt: StcsFstmtEnum): string {
  let dynamicRowId = ''
  if (fstmt === StcsFstmtEnum.SFSE_PNL) {
    dynamicRowId = findRowByEndsWith(fsd || [], 'total_output')
  } else if (fstmt === StcsFstmtEnum.SFSE_BS) {
    dynamicRowId = findRowByEndsWith(fsd || [], 'total_assets')
  }
  return dynamicRowId
}

export function getDynamicCtxLineRefId(fsd: PlanningRowData[] | StcsOrgFsdFlatItem[]): string {
  const outputRow = findRowByEndsWith(fsd, 'total_output_ctx_line')
  return outputRow ?? ''
}

export function getDynamicCashFlowTopIds(fsd: PlanningRowData[] | StcsOrgFsdFlatItem[]): string[] {
  const topIds: string[] = []
  const ebitdaRow = findRowByEndsWith(fsd, 'total_cash_flow_from_operations_ebitda')
  const taxRow = findRowByEndsWith(fsd, 'total_cash_flow_from_operations_level_1_tax_expense')
  if (ebitdaRow) topIds.push(ebitdaRow)
  if (taxRow) topIds.push(taxRow)
  return topIds
}

export function getDynamicRefRowId(ctxRowIds: string[], fallbackRefRowId: string): string {
  if (ctxRowIds && ctxRowIds.length > 0) {
    const revenueRow = ctxRowIds.find((id) => id.endsWith('_level_1_revenues'))
    if (revenueRow) return revenueRow

    const ebitdaRow = ctxRowIds.find((id) => id.endsWith('_total_ebitda'))
    if (ebitdaRow) return ebitdaRow
    return ctxRowIds[0]
  }
  return fallbackRefRowId
}

export function getMonthYearMaps(date: string, monthsToSubtract: number | null, ytd = false, addMonths = false): { currentMonthMap: string[]; previousMonthMap: string[] } {
  if (!date) {
    consola.debug('getMonthYearMaps(): Invalid date input: ', date)
    return { currentMonthMap: [], previousMonthMap: [] }
  }

  const [year, month] = date.split('-').map(Number) // Parse year and month from input date
  const ytdMonth = getCompEndMonth() // Get fiscal end month (e.g., 6 for June)
  const startMonth = ytdMonth % 12 // Fiscal start month (July if ytdMonth = 6)

  const currentMonthMap: string[] = []
  const previousMonthMap: string[] = []

  if (ytd) {
    // YTD Mode: Generate months from fiscal start month (July) to the given month
    // If the current month is before the fiscal start (July), we should adjust the year
    const current = moment({ year, month: startMonth }) // Start from fiscal year start month (e.g., July)
    const endMonth = moment({ year, month: month - 1 }) // End on the given month (converted to 0-indexed)

    // Check if current date is before fiscal year start, adjust year accordingly
    if (current.isAfter(endMonth)) {
      current.subtract(1, 'year') // Move to the previous fiscal year if needed
    }

    // Loop from startMonth to the given month (inclusive)
    while (current.isBefore(endMonth) || current.isSame(endMonth)) {
      const prev = current.clone().subtract(1, 'year') // Get the same month in the previous year
      currentMonthMap.push(current.format('YYYY-MM'))
      previousMonthMap.push(prev.format('YYYY-MM'))
      current.add(1, 'month') // Move to next month
    }
  } else if (monthsToSubtract != null) {
    // Normal mode: last N months
    for (let i = 0; i < monthsToSubtract; i++) {
      const current = moment({ year, month: month - 1 })
      if (addMonths) {
        current.add(monthsToSubtract - i, 'months')
      } else {
        current.subtract(monthsToSubtract - 1 - i, 'months')
      }
      const prev = current.clone().subtract(1, 'years')

      const currentStr = `${current.year()}-${String(current.month() + 1).padStart(2, '0')}`
      const prevStr = `${prev.year()}-${String(prev.month() + 1).padStart(2, '0')}`

      currentMonthMap.push(currentStr)
      previousMonthMap.push(prevStr)
    }
  }

  return { currentMonthMap, previousMonthMap }
}

export function mergeActualsAndPlanValues(actualValues: { [key: string]: StcsCompActualsResValuesItem }, planValues: { [key: string]: StcsCompPlanResValuesItem }, lastActual: string) {
  const data = Object.entries(actualValues).map(([key, val]) => {
    const lastActualMoment = moment(lastActual, 'YYYY-MM')
    const actValues = Object.entries(val?.values_map || [])?.filter(([date]) => moment(date, 'YYYY-MM').isSameOrBefore(lastActualMoment)) || []
    const rfValues = Object.entries(planValues?.[key]?.values_map || [])?.filter(([date]) => moment(date, 'YYYY-MM').isAfter(lastActualMoment)) || []
    return [key, { values_map: { ...Object.fromEntries(actValues), ...Object.fromEntries(rfValues) } }]
  })

  return Object.fromEntries(data)
}

export function calculateYTDAndYTG(date: string): { ytd: string[]; ytg: string[]; previousYtd: string[]; previousYtg: string[] } {
  const currentYear = moment(date).year()
  const currentMonth = moment(date).month() + 1
  const fiscalEndMonth = getCompEndMonth()
  const fiscalStartMonth = fiscalEndMonth === 12 ? 1 : fiscalEndMonth + 1

  const ytd: string[] = []
  const ytg: string[] = []

  let fiscalYearStartYear = currentYear
  if (fiscalEndMonth !== 12 && currentMonth <= fiscalEndMonth) {
    fiscalYearStartYear = currentYear - 1
  }

  const monthsElapsed = (currentYear - fiscalYearStartYear) * 12 + currentMonth - fiscalStartMonth + 1

  for (let i = 0; i < monthsElapsed; i++) {
    const m = moment({ year: fiscalYearStartYear, month: fiscalStartMonth - 1 })
      .add(i, 'months')
      .format('YYYY-MM')
    ytd.push(m)
  }

  const totalFiscalMonths = 12
  const monthsRemaining = totalFiscalMonths - ytd.length
  for (let i = 0; i < monthsRemaining; i++) {
    const m = moment(ytd[ytd.length - 1], 'YYYY-MM')
      .clone()
      .add(i + 1, 'months')
      .format('YYYY-MM')
    ytg.push(m)
  }

  const previousYtd: string[] = ytd?.map((d) => moment(d, 'YYYY-MM').clone().subtract(1, 'year').format('YYYY-MM'))
  const previousYtg: string[] = ytg?.map((d) => moment(d, 'YYYY-MM').clone().subtract(1, 'year').format('YYYY-MM'))

  return {
    ytd,
    ytg,
    previousYtd,
    previousYtg,
  }
}

export function buildPercentOfSubMenu(params: GetContextMenuItemsParams<FinancialsGridRowData> | GetMainMenuItemsParams, onChange: (data: StcsOrgFsdFlatItem | undefined, mapKey: 'pnl' | 'bs' | 'cf') => void): MenuItemDef[] {
  const { $tolgee } = useNuxtApp()
  const ctxStore = useCtxStore()

  const fsdOrg = ctxStore.ref_comp_map[ctxStore.active_comp_id].ref_comp_actuals_res_fstmt
  const rawCf = ctxStore.ref_comp_map[ctxStore.active_comp_id].ref_comp_actuals_res_fstmt?.cf_list ?? []
  const cfClean = rawCf.filter((item: StcsOrgFsdFlatItem) => item.rowClass !== 'account-item' && item.rowClass !== 'tag-row' && item.rowClass !== 'account-item-delta')

  const topIds = ['cf_total_cash_flow_from_operations_ebitda', 'cf_total_cash_flow_from_operations_level_1_tax_expense']
  const topItems = topIds.map((id) => cfClean.find((item: StcsOrgFsdFlatItem) => item.row_id === id)).filter(Boolean) as StcsOrgFsdFlatItem[]
  const otherItems = cfClean.filter((item: StcsOrgFsdFlatItem) => !topIds.includes(item.row_id))

  const cfFinal: StcsOrgFsdFlatItem[] = [...topItems, ...otherItems]

  const makeGroup = (label: string, items: StcsOrgFsdFlatItem[], mapKey: 'pnl' | 'bs' | 'cf') => ({
    name: label,
    subMenu: flattenToMenu(buildPathTree(items || []), params, mapKey, onChange),
  })

  return [makeGroup($tolgee.t('general-terms-pnl'), fsdOrg?.pnl_list || [], 'pnl'), makeGroup($tolgee.t('general-terms-bs'), fsdOrg?.bs_list || [], 'bs'), makeGroup($tolgee.t('general-terms-cf'), cfFinal, 'cf')]
}

function flattenToMenu(
  nodes: TreeNode<StcsOrgFsdFlatItem>[],
  params: GetContextMenuItemsParams<FinancialsGridRowData> | GetMainMenuItemsParams,
  mapKey: 'pnl' | 'bs' | 'cf',
  onChange: (data: StcsOrgFsdFlatItem | undefined, mapKey: 'pnl' | 'bs' | 'cf') => void,
): MenuItemDef[] {
  return nodes
    .filter((n) => {
      const rc = n.data?.rowClass
      return rc !== 'total-cml-ctx-line-percent-child' && rc !== 'total-cml-ctx-line-percent-parent'
    })
    .map((n) => {
      const hasKids = n.children.length > 0
      const allowLevel = n?.data?.fpna_type === StcsFpnaTypeEnum.SFT_LEVEL_1
      const label = n.data ? getRowDisplayName(n.data).name : ''

      const isTotalRow = n.data?.rowClass === 'total-cml' || n.data?.rowClass === 'total-ncml' || n.data?.rowClass === 'total-cml-delta'
      const displayName = label
      const item: MenuItemDef = { name: displayName }
      if (isTotalRow) {
        item.cssClasses = ['font-bold']
      }

      if (n.data) {
        item.action = () => onChange(n.data, mapKey)
      }
      if (hasKids && allowLevel) {
        item.subMenu = flattenToMenu(n.children, params, mapKey, onChange)
      }
      return item
    })
}

export function useStatelyInspector(active = false): ReturnType<typeof createBrowserInspector> | null {
  const isDev = process.env.NODE_ENV === 'development'

  const statelyInspector = isDev && active ? createBrowserInspector() : null

  return statelyInspector
}
